import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import {
  Form,
  Col,
  Button,
  Card,
  Modal,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MdDeleteForever } from 'react-icons/md';
import history from '../../../services/history';
import { liberarLaudoByPac } from '../../../services/prontuario';
import { deleteScan, getScan, saveScan } from '../../../services/carotidas';
import { carotidas } from '../../../helpers/laudos/carotidas';
import CustomFileInput from '../../../components/CustomFileInput';
import useUploadFiles from '../../../hooks/useUploadFiles';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import ModalCarotida from './ModalCarotida';

export default function FormCarotida() {
  const { id } = useParams();
  const codLaudo = Number(id);
  const codLaudoValido = Number.isInteger(codLaudo) && codLaudo > 0;
  const {
    data: carotida,
    error,
    isLoading,
    mutate: revalidarCarotida,
  } = useSWR(
    codLaudoValido ? ['carotida', codLaudo] : null,
    () => getScan(codLaudo),
    { revalidateOnFocus: false }
  );
  const [showModal, setShowModal] = useState(false);
  const [defaultValues, setDefaultValues] = useState(null);
  const [gravado, setGravado] = useState('');
  const [rascunhoId, setRascunhoId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const valuesRef = useRef([]);

  const {
    files,
    setFiles,
    showUpload,
    setShowUpload,
    isUploading,
    confirmUpload,
    handleCloseModal,
  } = useUploadFiles({ atividade: carotida?.carotida[0] });

  useEffect(() => {
    const exame = carotida?.carotida?.[0];
    if (!exame || Number(exame.CODSCAN) !== codLaudo) return;

    setDefaultValues({
      ...carotida,
      carotida: [{ ...exame }, ...(carotida.carotida.slice(1) || [])],
    });
    setGravado(exame.GRAVADO ?? '');
    setRascunhoId(codLaudo);
  }, [carotida, codLaudo]);

  const deleteLocalItem = (index, tipo) => {
    setDefaultValues(prev => ({
      ...prev,
      [tipo]: prev[tipo].filter((_, i) => i !== index),
    }));
  };

  const deleteScanFromDB = async (item, type) => {
    try {
      await deleteScan(item, type);
      revalidarCarotida();
    } catch (err) {
      toast.error(err.response.data.message);
    }
  };

  const handleExcluirLinha = (item, index, tipo) => {
    if (item?.novo || gravado !== 'SIM') {
      deleteLocalItem(index, tipo);
      return;
    }
    deleteScanFromDB(item, tipo);
  };

  const goToRowAbove = tipo => {
    const focusMap = {
      cid: 'ccdCodAnatomia',
      ced: 'cidCodAnatomia',
      avd: 'cedCodAnatomia',
      cce: 'avdCodAnatomia',
      cie: 'cceCodAnatomia',
      cee: 'cieCodAnatomia',
      ave: 'ceeCodAnatomia',
    };

    const prevElementId = focusMap[tipo];
    if (prevElementId) document.getElementById(prevElementId)?.focus();
  };

  const goToRowBelow = (event, tipo) => {
    event.persist();
    event.preventDefault();
    if (event.key === 'Enter') {
      const element = document.getElementById(tipo);
      if (element) {
        element.focus();
        element.select();
      }
    }
  };

  const goToRowBelowOnArrowDown = tipo => {
    setDefaultValues(prevState => ({
      ...prevState,
      [tipo]: [...prevState[tipo], valuesRef.current[tipo]],
    }));

    const focusMap = {
      ccd: 'cidCodAnatomia',
      cid: 'cedCodAnatomia',
      ced: 'avdCodAnatomia',
      avd: 'cceCodAnatomia',
      cce: 'cieCodAnatomia',
      cie: 'ceeCodAnatomia',
      cee: 'aveCodAnatomia',
    };

    const nextElementId = focusMap[tipo];
    if (nextElementId) document.getElementById(nextElementId)?.focus();
  };

  // Função para navegação condicional no "ArrowRight"
  const handleRightNavigation = (codAnatomia, tipo) => {
    const nextElementId =
      codAnatomia === 44 ? `${tipo}Medida` : `${tipo}CodVelocidade`;
    const nextElement = document.getElementById(nextElementId);

    if (nextElement) nextElement.focus();
  };

  const handleAnatomia = (event, tipo) => {
    event.persist(); // Mantém o evento ativo para acesso posterior
    event.preventDefault();

    const { key, target } = event;
    if (!['ArrowUp', 'ArrowRight', 'ArrowDown', 'Enter'].includes(key)) return;

    // Encontra a anatomia correspondente ao valor do input
    const anatomia = carotidas.scan.anatomia.find(
      a => a.CODANATOMIA === Number(target.value)
    );

    if (!anatomia) return;

    // Atualiza o estado dos valores atuais
    valuesRef.current = {
      tipo,
      CODSCAN: codLaudo,
      [tipo]: {
        ...valuesRef.current[tipo],
        ANATOMIA: anatomia.ANATOMIA ?? null,
        CODANATOMIA: anatomia.CODANATOMIA ?? null,
        CODSCAN: codLaudo,
        novo: true,
      },
    };

    switch (key) {
      case 'ArrowUp':
        goToRowAbove(tipo);
        break;
      case 'ArrowRight':
        handleRightNavigation(anatomia.CODANATOMIA, tipo);
        break;
      case 'ArrowDown':
      case 'Enter':
        goToRowBelowOnArrowDown(tipo);
        break;
      default:
        break;
    }
  };

  const handleMedida = (e, tipo) => {
    e.persist();
    e.preventDefault();

    if (!['ArrowUp', 'ArrowRight', 'ArrowDown', 'Enter'].includes(e.key))
      return;

    if (['ArrowUp', 'ArrowRight', 'ArrowDown', 'Enter'].includes(e.key)) {
      valuesRef.current = {
        ...valuesRef.current,
        tipo,
        CODSCAN: codLaudo,
        [tipo]: {
          ...valuesRef.current[tipo],
          MEDIDA: e.target?.value ?? null,
        },
      };

      if (e.key === 'ArrowUp') {
        goToRowAbove(tipo);
      } else if (e.key === 'ArrowRight') {
        document.getElementById(`${tipo}CodVelocidade`).focus();
      } else if (e.key === 'ArrowDown' || e.key === 'Enter') {
        goToRowBelowOnArrowDown(tipo);
      }
    }
  };

  const handleVelocidade = async (event, tipo) => {
    event.persist();
    event.preventDefault();

    if (['ArrowUp', 'ArrowRight', 'ArrowDown', 'Enter'].includes(event.key)) {
      const velocidade = carotidas.scan.velocidade.find(
        v => v.CODVELOCIDADE === Number(event.target.value)
      );

      if (!velocidade) return;

      valuesRef.current = {
        tipo,
        CODSCAN: codLaudo,
        [tipo]: {
          ...valuesRef.current[tipo],
          VELOCIDADE: velocidade.VELOCIDADE ?? null,
          CODVELOCIDADE: velocidade.CODVELOCIDADE ?? null,
          CODSCAN: codLaudo,
        },
      };

      if (event.key === 'ArrowUp') {
        goToRowAbove(tipo);
      } else if (event.key === 'ArrowRight') {
        document.getElementById(`${tipo}CodIntensidade`).focus();
      } else if (event.key === 'ArrowDown' || event.key === 'Enter') {
        goToRowBelowOnArrowDown(tipo);
      }
    }
  };

  const handleIntensidade = async (event, tipo) => {
    event.persist();
    event.preventDefault();

    if (['ArrowUp', 'ArrowRight', 'ArrowDown', 'Enter'].includes(event.key)) {
      const intensidade = carotidas.scan.intensidade.find(
        i => i.CODINTENSIDADE === Number(event.target.value)
      );

      if (!intensidade) return;

      valuesRef.current = {
        tipo,
        CODSCAN: codLaudo,
        [tipo]: {
          ...valuesRef.current[tipo],
          INTENSIDADE: intensidade.INTENSIDADE ?? null,
          CODINTENSIDADE: intensidade.CODINTENSIDADE ?? null,
          CODSCAN: codLaudo,
        },
      };

      if (event.key === 'ArrowUp') {
        goToRowAbove(tipo);
      } else if (event.key === 'ArrowRight') {
        document.getElementById(`${tipo}CodAnatomia`).focus();
      } else if (event.key === 'ArrowDown' || event.key === 'Enter') {
        goToRowBelowOnArrowDown(tipo);
      }
    }
  };

  const handleGravar = async () => {
    try {
      setIsUpdating(true);
      const data = await saveScan(defaultValues);
      revalidarCarotida();
      toast.success(data.message);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Falha ao gravar. Verifique sua conexão e tente novamente.';
      toast.error(message);
    } finally {
      setIsUpdating(false);
    }
  };

  async function handleLiberarLaudo() {
    try {
      const exame = carotida?.carotida?.[0];
      const message = await liberarLaudoByPac({
        fatura: {
          codLaudo: exame?.CODSCAN,
          codPac: exame?.CODPAC,
        },
      });
      toast.success(message);
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          'Erro ao liberar laudo!'
      );
    }
  }

  const handleDefaultAnatomia = () => {
    setDefaultValues(prevValues => ({
      ...prevValues,
      ccd: [
        {
          ...(prevValues.ccd?.[0] || {}),
          ANATOMIA: 'Anatomia e fluxos normais.',
          CODANATOMIA: 1,
          INTENSIDADE: null,
          CODINTENSIDADE: null,
          VELOCIDADE: null,
          CODVELOCIDADE: null,
          CODSCAN: codLaudo,
          novo: true,
        },
      ],
      cid: [
        {
          ...(prevValues.cid?.[0] || {}),
          ANATOMIA: 'Anatomia e fluxos normais.',
          CODANATOMIA: 1,
          INTENSIDADE: null,
          CODINTENSIDADE: null,
          VELOCIDADE: null,
          CODVELOCIDADE: null,
          CODSCAN: codLaudo,
          novo: true,
        },
      ],
      ced: [
        {
          ...(prevValues.ced?.[0] || {}),
          ANATOMIA: 'Anatomia e fluxos normais.',
          CODANATOMIA: 1,
          INTENSIDADE: null,
          CODINTENSIDADE: null,
          VELOCIDADE: null,
          CODVELOCIDADE: null,
          CODSCAN: codLaudo,
          novo: true,
        },
      ],
      avd: [
        {
          ...(prevValues.avd?.[0] || {}),
          ANATOMIA: 'Anatomia e fluxos normais.',
          CODANATOMIA: 1,
          INTENSIDADE: null,
          CODINTENSIDADE: null,
          VELOCIDADE: null,
          CODVELOCIDADE: null,
          CODSCAN: codLaudo,
          novo: true,
        },
      ],
      cce: [
        {
          ...(prevValues.cce?.[0] || {}),
          ANATOMIA: 'Anatomia e fluxos normais.',
          CODANATOMIA: 1,
          INTENSIDADE: null,
          CODINTENSIDADE: null,
          VELOCIDADE: null,
          CODVELOCIDADE: null,
          CODSCAN: codLaudo,
          novo: true,
        },
      ],
      ave: [
        {
          ...(prevValues.ave?.[0] || {}),
          ANATOMIA: 'Anatomia e fluxos normais.',
          CODANATOMIA: 1,
          INTENSIDADE: null,
          CODINTENSIDADE: null,
          VELOCIDADE: null,
          CODVELOCIDADE: null,
          CODSCAN: codLaudo,
          novo: true,
        },
      ],
      cie: [
        {
          ...(prevValues.cie?.[0] || {}),
          ANATOMIA: 'Anatomia e fluxos normais.',
          CODANATOMIA: 1,
          INTENSIDADE: null,
          CODINTENSIDADE: null,
          VELOCIDADE: null,
          CODVELOCIDADE: null,
          CODSCAN: codLaudo,
          novo: true,
        },
      ],
      cee: [
        {
          ...(prevValues.cee?.[0] || {}),
          ANATOMIA: 'Anatomia e fluxos normais.',
          CODANATOMIA: 1,
          INTENSIDADE: null,
          CODINTENSIDADE: null,
          VELOCIDADE: null,
          CODVELOCIDADE: null,
          CODSCAN: codLaudo,
          novo: true,
        },
      ],
      carotida: [
        {
          ...prevValues.carotida[0],
          CODSCAN: codLaudo,
          OBSERVACOES: prevValues.carotida[0].OBSERVACOES || '',
          novo: true,
        },
        ...(prevValues.carotida?.slice(1) || []),
      ],
    }));
  };

  if (!codLaudoValido) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Laudo não encontrado.</Alert.Heading>
        <p>Abra o exame pela agenda.</p>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  if (isLoading || rascunhoId !== codLaudo || !defaultValues) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  return (
    <>
      <Card className="mt-2">
        <LaudoCardHeader title="ULTRASSOM DOPPLER DE CARÓTIDAS" />
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '8px 16px 0 16px',
          }}
        >
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowModal(true)}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <span style={{ marginRight: 6 }}>
              <svg
                width="1em"
                height="1em"
                viewBox="0 0 16 16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10 1.5A1.5 1.5 0 0 1 11.5 3v1h-7V3A1.5 1.5 0 0 1 6 1.5h4zm1.5 2A.5.5 0 0 0 11 3V1.5A2.5 2.5 0 0 0 8.5-1h-4A2.5 2.5 0 0 0 2 1.5V3a.5.5 0 0 0-.5.5v10A2.5 2.5 0 0 0 4 16h8a2.5 2.5 0 0 0 2.5-2.5v-10A.5.5 0 0 0 14 3V1.5A1.5 1.5 0 0 0 12.5 0h-4A1.5 1.5 0 0 0 7 1.5V3a.5.5 0 0 0-.5.5v10A1.5 1.5 0 0 0 8 15h4a1.5 1.5 0 0 0 1.5-1.5v-10z" />
              </svg>
            </span>
            Copiar Laudo Anterior
          </Button>
        </div>
        <Card.Body>
          <Form noValidate>
            <strong className="mr-auto">Nome do Paciente: </strong>
            {`${defaultValues?.carotida[0]?.CODPAC} - ${defaultValues?.carotida[0]?.NOMEPAC}`}
            <br></br>
            <strong className="mr-auto">Data de Nasc: </strong>
            {`${defaultValues?.carotida[0]?.dataNasc}`}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Sexo: </strong>
            {defaultValues?.carotida[0]?.SEXOPAC}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Idade: </strong>
            {`${defaultValues?.carotida[0]?.idadePAC} anos`}
            <br></br>
            <strong className="mr-auto">Convênio: </strong>
            {defaultValues?.carotida[0]?.SIGLACONV}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Cod Laudo: </strong>
            {defaultValues?.carotida[0]?.CODSCAN}
            &nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Data do Exame: </strong>
            {defaultValues?.carotida[0]?.dataScanFmt}
            <br></br>
            <strong className="mr-auto">Médico que Realizou: Dr. </strong>
            {defaultValues?.carotida[0]?.Realizou}
            <br></br>
            <strong className="mr-auto">Médico que Solicitou: Dr. </strong>
            {defaultValues?.carotida[0]?.Solicitante}
            <Card.Header className="text-center" style={{ marginTop: 5 }}>
              <strong>
                <u>SISTEMA CAROTÍDEO DIREITO</u>
              </strong>
            </Card.Header>
            <Card.Header className="text-center" style={{ marginTop: 2 }}>
              <Button onClick={handleDefaultAnatomia}>Padrão Anatomia</Button>
            </Card.Header>
            <Form.Group size="sm" controlId="ccd">
              <u className="mr-auto">Carótida Comum Direita: </u>
              <Form.Row>
                <Form.Group as={Col} sm controlId="ccdCodAnatomia">
                  <Form.Label>
                    <strong>Anatomia</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        // Ensure handleAnatomia finishes before going to the next row
                        await handleAnatomia(event, 'ccd');
                        goToRowBelow(event, 'cidCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleAnatomia(event, 'ccd');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="ccdMedida">
                  <Form.Label>
                    <strong>Medida</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleMedida(event, 'ccd');
                        goToRowBelow(event, 'cidCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleMedida(event, 'ccd');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="ccdCodVelocidade">
                  <Form.Label>
                    <strong>Velocidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleVelocidade(event, 'ccd');
                        goToRowBelow(event, 'cidCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleVelocidade(event, 'ccd');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="ccdCodIntensidade">
                  <Form.Label>
                    <strong>Intensidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleIntensidade(event, 'ccd');
                        goToRowBelow(event, 'cidCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleIntensidade(event, 'ccd');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </Form.Row>
              {Object.keys(defaultValues || {})
                .filter(tipo => tipo === 'ccd')
                .map(tipo => (
                  <div key={tipo}>
                    {Array.isArray(defaultValues[tipo]) &&
                      defaultValues[tipo]?.map(
                        (item, index) =>
                          item?.CODSCAN === codLaudo &&
                          (item?.CODANATOMIA === 44 ? (
                            <div key={index} className="row">
                              <div className="col-12">
                                {`${item?.CODANATOMIA ? item?.CODANATOMIA : ''
                                  }${item?.CODANATOMIA && item?.CODVELOCIDADE
                                    ? ' - '
                                    : ''
                                  }${item?.CODVELOCIDADE ? item?.CODVELOCIDADE : ''
                                  }${item?.CODVELOCIDADE && item?.CODINTENSIDADE
                                    ? ' - '
                                    : ''
                                  }${item?.CODINTENSIDADE
                                    ? item?.CODINTENSIDADE
                                    : ''
                                  }`}
                                : {item?.ANATOMIA} {item?.MEDIDA} mm{' '}
                                {item?.VELOCIDADE} {item?.INTENSIDADE} -{' '}
                                <MdDeleteForever
                                  color={item.novo ? 'blue' : 'red'}
                                  cursor="pointer"
                                  onClick={() => {
                                    handleExcluirLinha(item, index, tipo);
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <div key={index} className="row">
                              <div className="col-12">
                                {`${item?.CODANATOMIA ? item?.CODANATOMIA : ''
                                  }${item?.CODANATOMIA && item?.CODVELOCIDADE
                                    ? ' - '
                                    : ''
                                  }${item?.CODVELOCIDADE ? item?.CODVELOCIDADE : ''
                                  }${item?.CODVELOCIDADE && item?.CODINTENSIDADE
                                    ? ' - '
                                    : ''
                                  }${item?.CODINTENSIDADE
                                    ? item?.CODINTENSIDADE
                                    : ''
                                  }`}
                                : {item?.ANATOMIA} {item?.VELOCIDADE}{' '}
                                {item?.INTENSIDADE} -{' '}
                                <MdDeleteForever
                                  color={item?.novo ? 'blue' : 'red'}
                                  cursor="pointer"
                                  onClick={() => {
                                    handleExcluirLinha(item, index, tipo);
                                  }}
                                />
                              </div>
                            </div>
                          ))
                      )}
                  </div>
                ))}
            </Form.Group>
            <Form.Group size="sm" controlId="cid">
              <u className="mr-auto">Carótida Interna Direita: </u>
              <Form.Row>
                <Form.Group as={Col} sm controlId="cidCodAnatomia">
                  <Form.Label>
                    <strong>Anatomia</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        // Ensure handleAnatomia finishes before going to the next row
                        await handleAnatomia(event, 'cid');
                        goToRowBelow(event, 'cedCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleAnatomia(event, 'cid');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="cidCodVelocidade">
                  <Form.Label>
                    <strong>Velocidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        // Ensure handleAnatomia finishes before going to the next row
                        await handleVelocidade(event, 'cid');
                        goToRowBelow(event, 'cedCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleVelocidade(event, 'cid');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="cidCodIntensidade">
                  <Form.Label>
                    <strong>Intensidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        // Ensure handleAnatomia finishes before going to the next row
                        await handleIntensidade(event, 'cid');
                        goToRowBelow(event, 'cedCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleIntensidade(event, 'cid');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </Form.Row>
              {Object.keys(defaultValues || {})
                .filter(tipo => tipo === 'cid')
                .map(tipo => (
                  <div key={tipo}>
                    {defaultValues[tipo]?.map(
                      (item, index) =>
                        item?.CODSCAN === codLaudo && (
                          <div key={index} className="row">
                            <div className="col-12">
                              {`${item?.CODANATOMIA ? item?.CODANATOMIA : ''}${item?.CODANATOMIA && item?.CODVELOCIDADE
                                ? ' - '
                                : ''
                                }${item?.CODVELOCIDADE ? item?.CODVELOCIDADE : ''
                                }${item?.CODVELOCIDADE && item?.CODINTENSIDADE
                                  ? ' - '
                                  : ''
                                }${item?.CODINTENSIDADE ? item?.CODINTENSIDADE : ''
                                }`}
                              : {item?.ANATOMIA} {item?.VELOCIDADE}{' '}
                              {item?.INTENSIDADE} -{' '}
                              <MdDeleteForever
                                color={item.novo ? 'blue' : 'red'}
                                cursor="pointer"
                                onClick={() => {
                                  handleExcluirLinha(item, index, tipo);
                                }}
                              />
                            </div>
                          </div>
                        )
                    )}
                  </div>
                ))}
            </Form.Group>
            <Form.Group size="sm" controlId="ced">
              <u className="mr-auto">Carótida Externa Direita: </u>
              <Form.Row>
                <Form.Group as={Col} sm controlId="cedCodAnatomia">
                  <Form.Label>
                    <strong>Anatomia</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleAnatomia(event, 'ced');
                        goToRowBelow(event, 'avdCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleAnatomia(event, 'ced');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="cedCodVelocidade">
                  <Form.Label>
                    <strong>Velocidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleVelocidade(event, 'ced');
                        goToRowBelow(event, 'avdCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleVelocidade(event, 'ced');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="cedCodIntensidade">
                  <Form.Label>
                    <strong>Intensidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleIntensidade(event, 'ced');
                        goToRowBelow(event, 'avdCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleIntensidade(event, 'ced');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </Form.Row>
              {Object.keys(defaultValues || {})
                .filter(tipo => tipo === 'ced')
                .map(tipo => (
                  <div key={tipo}>
                    {defaultValues[tipo]?.map(
                      (item, index) =>
                        item?.CODSCAN === codLaudo && (
                          <div key={index} className="row">
                            <div className="col-12">
                              {`${item?.CODANATOMIA ? item?.CODANATOMIA : ''}${item?.CODANATOMIA && item?.CODVELOCIDADE
                                ? ' - '
                                : ''
                                }${item?.CODVELOCIDADE ? item?.CODVELOCIDADE : ''
                                }${item?.CODVELOCIDADE && item?.CODINTENSIDADE
                                  ? ' - '
                                  : ''
                                }${item?.CODINTENSIDADE ? item?.CODINTENSIDADE : ''
                                }`}
                              : {item?.ANATOMIA} {item?.VELOCIDADE}{' '}
                              {item?.INTENSIDADE} -{' '}
                              <MdDeleteForever
                                color={item.novo ? 'blue' : 'red'}
                                cursor="pointer"
                                onClick={() => {
                                  handleExcluirLinha(item, index, tipo);
                                }}
                              />
                            </div>
                          </div>
                        )
                    )}
                  </div>
                ))}
            </Form.Group>
            <Form.Group size="sm" controlId="avd">
              <u className="mr-auto">Artéria Vertebral Direita: </u>
              <Form.Row>
                <Form.Group as={Col} sm controlId="avdCodAnatomia">
                  <Form.Label>
                    <strong>Anatomia</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleAnatomia(event, 'avd');
                        goToRowBelow(event, 'cceCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleAnatomia(event, 'avd');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="avdCodVelocidade">
                  <Form.Label>
                    <strong>Velocidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleVelocidade(event, 'avd');
                        goToRowBelow(event, 'cceCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleVelocidade(event, 'avd');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="avdCodIntensidade">
                  <Form.Label>
                    <strong>Intensidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleIntensidade(event, 'avd');
                        goToRowBelow(event, 'cceCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleIntensidade(event, 'avd');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </Form.Row>
              {Object.keys(defaultValues || {})
                .filter(tipo => tipo === 'avd')
                .map(tipo => (
                  <div key={tipo}>
                    {defaultValues[tipo]?.map(
                      (item, index) =>
                        item?.CODSCAN === codLaudo && (
                          <div key={index} className="row">
                            <div className="col-12">
                              {`${item?.CODANATOMIA ? item?.CODANATOMIA : ''}${item?.CODANATOMIA && item?.CODVELOCIDADE
                                ? ' - '
                                : ''
                                }${item?.CODVELOCIDADE ? item?.CODVELOCIDADE : ''
                                }${item?.CODVELOCIDADE && item?.CODINTENSIDADE
                                  ? ' - '
                                  : ''
                                }${item?.CODINTENSIDADE ? item?.CODINTENSIDADE : ''
                                }`}
                              : {item?.ANATOMIA} {item?.VELOCIDADE}{' '}
                              {item?.INTENSIDADE} -{' '}
                              <MdDeleteForever
                                color={item.novo ? 'blue' : 'red'}
                                cursor="pointer"
                                onClick={() => {
                                  handleExcluirLinha(item, index, tipo);
                                }}
                              />
                            </div>
                          </div>
                        )
                    )}
                  </div>
                ))}
            </Form.Group>
            <Card.Header className="text-center">
              <strong>
                <u>SISTEMA CAROTÍDEO ESQUERDO</u>
              </strong>
            </Card.Header>
            <Form.Group size="sm" controlId="cce">
              <u className="mr-auto">Carótida Comum Esquerda: </u>
              <Form.Row>
                <Form.Group as={Col} sm controlId="cceCodAnatomia">
                  <Form.Label>
                    <strong>Anatomia</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleAnatomia(event, 'cce');
                        goToRowBelow(event, 'cieCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleAnatomia(event, 'cce');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="cceMedida">
                  <Form.Label>
                    <strong>Medida</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleMedida(event, 'cce');
                        goToRowBelow(event, 'cieCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleMedida(event, 'cce');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="cceCodVelocidade">
                  <Form.Label>
                    <strong>Velocidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleVelocidade(event, 'cce');
                        goToRowBelow(event, 'cieCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleVelocidade(event, 'cce');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="cceCodIntensidade">
                  <Form.Label>
                    <strong>Intensidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleIntensidade(event, 'cce');
                        goToRowBelow(event, 'cieCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleIntensidade(event, 'cce');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </Form.Row>
              {Object.keys(defaultValues || {})
                .filter(tipo => tipo === 'cce')
                .map(tipo => (
                  <div key={tipo}>
                    {defaultValues[tipo]?.map(
                      (item, index) =>
                        item?.CODSCAN === codLaudo &&
                        (item?.CODANATOMIA === 44 ? (
                          <div key={index} className="row">
                            <div className="col-12">
                              {`${item?.CODANATOMIA ? item?.CODANATOMIA : ''}${item?.CODANATOMIA && item?.CODVELOCIDADE
                                ? ' - '
                                : ''
                                }${item?.CODVELOCIDADE ? item?.CODVELOCIDADE : ''
                                }${item?.CODVELOCIDADE && item?.CODINTENSIDADE
                                  ? ' - '
                                  : ''
                                }${item?.CODINTENSIDADE ? item?.CODINTENSIDADE : ''
                                }`}
                              : {item?.ANATOMIA} {item?.MEDIDA} mm{' '}
                              {item?.VELOCIDADE} {item?.INTENSIDADE} -{' '}
                              <MdDeleteForever
                                color={item.novo ? 'blue' : 'red'}
                                cursor="pointer"
                                onClick={() => {
                                  handleExcluirLinha(item, index, tipo);
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div key={index} className="row">
                            <div className="col-12">
                              {`${item?.CODANATOMIA ? item?.CODANATOMIA : ''}${item?.CODANATOMIA && item?.CODVELOCIDADE
                                ? ' - '
                                : ''
                                }${item?.CODVELOCIDADE ? item?.CODVELOCIDADE : ''
                                }${item?.CODVELOCIDADE && item?.CODINTENSIDADE
                                  ? ' - '
                                  : ''
                                }${item?.CODINTENSIDADE ? item?.CODINTENSIDADE : ''
                                }`}
                              : {item?.ANATOMIA} {item?.VELOCIDADE}{' '}
                              {item?.INTENSIDADE} -{' '}
                              <MdDeleteForever
                                color={item.novo ? 'blue' : 'red'}
                                cursor="pointer"
                                onClick={() => {
                                  handleExcluirLinha(item, index, tipo);
                                }}
                              />
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                ))}
            </Form.Group>
            <Form.Group size="sm" controlId="cie">
              <u className="mr-auto">Carótida Interna Esquerda: </u>
              <Form.Row>
                <Form.Group as={Col} sm controlId="cieCodAnatomia">
                  <Form.Label>
                    <strong>Anatomia</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleAnatomia(event, 'cie');
                        goToRowBelow(event, 'ceeCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleAnatomia(event, 'cie');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="cieCodVelocidade">
                  <Form.Label>
                    <strong>Velocidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleVelocidade(event, 'cie');
                        goToRowBelow(event, 'ceeCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleVelocidade(event, 'cie');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="cieCodIntensidade">
                  <Form.Label>
                    <strong>Intensidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleIntensidade(event, 'cie');
                        goToRowBelow(event, 'ceeCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleIntensidade(event, 'cie');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </Form.Row>
              {Object.keys(defaultValues || {})
                .filter(tipo => tipo === 'cie')
                .map(tipo => (
                  <div key={tipo}>
                    {defaultValues[tipo]?.map(
                      (item, index) =>
                        item?.CODSCAN === codLaudo && (
                          <div key={index} className="row">
                            <div className="col-12">
                              {`${item?.CODANATOMIA ? item?.CODANATOMIA : ''}${item?.CODANATOMIA && item?.CODVELOCIDADE
                                ? ' - '
                                : ''
                                }${item?.CODVELOCIDADE ? item?.CODVELOCIDADE : ''
                                }${item?.CODVELOCIDADE && item?.CODINTENSIDADE
                                  ? ' - '
                                  : ''
                                }${item?.CODINTENSIDADE ? item?.CODINTENSIDADE : ''
                                }`}
                              : {item?.ANATOMIA} {item?.VELOCIDADE}{' '}
                              {item?.INTENSIDADE} -{' '}
                              <MdDeleteForever
                                color={item.novo ? 'blue' : 'red'}
                                cursor="pointer"
                                onClick={() => {
                                  handleExcluirLinha(item, index, tipo);
                                }}
                              />
                            </div>
                          </div>
                        )
                    )}
                  </div>
                ))}
            </Form.Group>
            <Form.Group size="sm" controlId="cee">
              <u className="mr-auto">Carótida Externa Esquerda: </u>
              <Form.Row>
                <Form.Group as={Col} sm controlId="ceeCodAnatomia">
                  <Form.Label>
                    <strong>Anatomia</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleAnatomia(event, 'cee');
                        goToRowBelow(event, 'aveCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleAnatomia(event, 'cee');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="ceeCodVelocidade">
                  <Form.Label>
                    <strong>Velocidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleVelocidade(event, 'cee');
                        goToRowBelow(event, 'aveCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleVelocidade(event, 'cee');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="ceeCodIntensidade">
                  <Form.Label>
                    <strong>Intensidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleIntensidade(event, 'cee');
                        goToRowBelow(event, 'aveCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleIntensidade(event, 'cee');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </Form.Row>
              {Object.keys(defaultValues || {})
                .filter(tipo => tipo === 'cee')
                .map(tipo => (
                  <div key={tipo}>
                    {defaultValues[tipo]?.map(
                      (item, index) =>
                        item?.CODSCAN === codLaudo && (
                          <div key={index} className="row">
                            <div className="col-12">
                              {`${item?.CODANATOMIA ? item?.CODANATOMIA : ''}${item?.CODANATOMIA && item?.CODVELOCIDADE
                                ? ' - '
                                : ''
                                }${item?.CODVELOCIDADE ? item?.CODVELOCIDADE : ''
                                }${item?.CODVELOCIDADE && item?.CODINTENSIDADE
                                  ? ' - '
                                  : ''
                                }${item?.CODINTENSIDADE ? item?.CODINTENSIDADE : ''
                                }`}
                              : {item?.ANATOMIA} {item?.VELOCIDADE}{' '}
                              {item?.INTENSIDADE} -{' '}
                              <MdDeleteForever
                                color={item.novo ? 'blue' : 'red'}
                                cursor="pointer"
                                onClick={() => {
                                  handleExcluirLinha(item, index, tipo);
                                }}
                              />
                            </div>
                          </div>
                        )
                    )}
                  </div>
                ))}
            </Form.Group>
            <Form.Group size="sm" controlId="ave">
              <u className="mr-auto">Artéria Vertebral Esquerda: </u>
              <Form.Row>
                <Form.Group as={Col} sm controlId="aveCodAnatomia">
                  <Form.Label>
                    <strong>Anatomia</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleAnatomia(event, 'ave');
                        goToRowBelow(event, 'aveCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleAnatomia(event, 'ave');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="aveCodVelocidade">
                  <Form.Label>
                    <strong>Velocidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleVelocidade(event, 'ave');
                        goToRowBelow(event, 'aveCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleVelocidade(event, 'ave');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group as={Col} sm controlId="aveCodIntensidade">
                  <Form.Label>
                    <strong>Intensidade</strong>
                  </Form.Label>
                  <Form.Control
                    onKeyDown={async event => {
                      if (event.key === 'ArrowDown' || event.key === 'Enter') {
                        await handleIntensidade(event, 'ave');
                        goToRowBelow(event, 'aveCodAnatomia');
                      }
                      if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
                        handleIntensidade(event, 'ave');
                      }
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </Form.Row>
              {Object.keys(defaultValues || {})
                .filter(tipo => tipo === 'ave')
                .map(tipo => (
                  <div key={tipo}>
                    {defaultValues[tipo]?.map(
                      (item, index) =>
                        item?.CODSCAN === codLaudo && (
                          <div key={index} className="row">
                            <div className="col-12">
                              {`${item?.CODANATOMIA ? item?.CODANATOMIA : ''}${item?.CODANATOMIA && item?.CODVELOCIDADE
                                ? ' - '
                                : ''
                                }${item?.CODVELOCIDADE ? item?.CODVELOCIDADE : ''
                                }${item?.CODVELOCIDADE && item?.CODINTENSIDADE
                                  ? ' - '
                                  : ''
                                }${item?.CODINTENSIDADE ? item?.CODINTENSIDADE : ''
                                }`}
                              : {item?.ANATOMIA} {item?.VELOCIDADE}{' '}
                              {item?.INTENSIDADE} -{' '}
                              <MdDeleteForever
                                color={item.novo ? 'blue' : 'red'}
                                cursor="pointer"
                                onClick={() => {
                                  handleExcluirLinha(item, index, tipo);
                                }}
                              />
                            </div>
                          </div>
                        )
                    )}
                  </div>
                ))}
            </Form.Group>
            <Form.Group size="sm" controlId="editDescricao">
              <strong>OBSERVAÇÕES</strong>
              <Form.Control
                as="textarea"
                value={defaultValues?.carotida[0]?.OBSERVACOES || ''}
                onChange={e => {
                  e.persist();
                  setDefaultValues(prevValues => ({
                    ...prevValues,
                    carotida: [
                      {
                        ...prevValues.carotida[0],
                        OBSERVACOES: e.target.value,
                      },
                      ...prevValues.carotida.slice(1),
                    ],
                  }));
                }}
              />
            </Form.Group>
            <Card.Footer className="text-center">
              <Button
                variant="success"
                disabled={isUpdating}
                onClick={handleGravar}
              >
                {isUpdating ? (
                  <Spinner animation="border" variant="dark" size="sm" />
                ) : (
                  'Gravar'
                )}
              </Button>{' '}
              <Button onClick={handleLiberarLaudo}>Liberar Laudo</Button>{' '}
              <Button
                variant="secondary"
                onClick={() => {
                  history.push({
                    pathname: `/report-carotida/${carotida.carotida[0].CODSCAN}`,
                    state: { printing: false },
                  });
                }}
              >
                Imprimir
              </Button>{' '}
              <Button
                variant="secondary"
                onClick={() => {
                  history.push({
                    pathname: `/report-carotida/${carotida.carotida[0].CODSCAN}`,
                    state: { printing: true },
                  });
                }}
              >
                Imprimir Timbrado
              </Button>{' '}
              <Button variant="warning" onClick={() => setShowUpload(true)}>
                Anexar
              </Button>{' '}
              <Button
                variant="info"
                onClick={() => {
                  history.push(
                    `/imagem-carotida/${defaultValues?.carotida?.[0]?.CODSCAN}`
                  );
                }}
              >
                Editar Imagem
              </Button>{' '}
              {/* talvez seria legal enviar o id do médico nesses Buttons "Ir para Agenda"
              aí o médico já volta pra sua própria agenda do dia */}
              <Button variant="info" onClick={() => history.push('/agenda')}>
                Ir para Agenda
              </Button>
            </Card.Footer>
          </Form>
        </Card.Body>
      </Card>

      <Modal show={showUpload} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Anexar arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomFileInput
            id="file-upload"
            label="Selecionar arquivo"
            multiple={true}
            onChange={e => setFiles(Array.from(e.target.files))}
          />
          <ul>
            {files && files.map(({ name }) => <li key={name}>{name}</li>)}
          </ul>{' '}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={confirmUpload} disabled={isUploading}>
            {isUploading ? 'Enviando...' : 'Confirmar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalCarotida
        show={showModal}
        onHide={() => setShowModal(false)}
        codPac={defaultValues?.carotida[0]?.CODPAC}
        onCopy={data => {
          const scanArrays = [
            'ccd',
            'cid',
            'ced',
            'avd',
            'cce',
            'cie',
            'cee',
            'ave',
          ];
          const copied = {};
          scanArrays.forEach(tipo => {
            if (Array.isArray(data[tipo])) {
              copied[tipo] = data[tipo].map(item => ({
                ...item,
                CODSCAN: codLaudo,
                novo: true,
              }));
            }
          });
          setDefaultValues(prev => ({
            ...prev,
            ...copied,
            carotida: [
              {
                ...prev.carotida[0],
                OBSERVACOES: data?.carotida?.[0]?.OBSERVACOES || '',
                IMAGEM: data?.carotida?.[0]?.IMAGEM || '',
              },
              ...(prev.carotida?.slice(1) || []),
            ],
          }));
          setShowModal(false);
        }}
      />
    </>
  );
}
